import React, { useState } from 'react';
import { 
  getUserApplications, 
  getBoxToAppMap, 
  hasMultipleApplications,
  getApplicationDisplayName,
  debugMultiAppState 
} from './multiAppUtils';

/**
 * Debug component to show multi-app state during development
 * Only shows in development mode
 */
const MultiAppDebug = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const userApps = getUserApplications();
  const boxToAppMap = getBoxToAppMap();
  const hasMultiApps = hasMultipleApplications();

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="bg-purple-600 text-white px-3 py-2 rounded-lg text-xs font-mono shadow-lg hover:bg-purple-700 transition-colors"
      >
        🔧 Multi-App Debug {hasMultiApps ? `(${userApps.length})` : '(1)'}
      </button>
      
      {isExpanded && (
        <div className="absolute bottom-12 right-0 bg-white border border-gray-300 rounded-lg shadow-xl p-4 w-96 max-h-96 overflow-y-auto text-xs font-mono">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-sm">Multi-App Debug Info</h3>
            <button
              onClick={() => debugMultiAppState()}
              className="bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600"
            >
              Log to Console
            </button>
          </div>
          
          <div className="space-y-3">
            {/* User Applications */}
            <div>
              <h4 className="font-semibold text-gray-700 mb-1">User Applications ({userApps.length})</h4>
              {userApps.length === 0 ? (
                <div className="text-gray-500 italic">No applications found</div>
              ) : (
                <div className="space-y-2">
                  {userApps.map((app, index) => (
                    <div key={index} className="bg-gray-50 p-2 rounded border">
                      <div className="font-semibold text-blue-600">{app.app_id}</div>
                      <div className="text-gray-600">Email: {app.email}</div>
                      <div className="text-gray-600">Arbox: {app.arbox_email}</div>
                      <div className="text-gray-600">Display: {getApplicationDisplayName(app.app_id)}</div>
                      <div className="text-gray-500 text-xs">JWT: {app.jwt_token ? '✓' : '✗'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Box to App Mapping */}
            <div>
              <h4 className="font-semibold text-gray-700 mb-1">Box → App Mapping</h4>
              {Object.keys(boxToAppMap).length === 0 ? (
                <div className="text-gray-500 italic">No mapping found</div>
              ) : (
                <div className="bg-gray-50 p-2 rounded border">
                  {Object.entries(boxToAppMap).map(([boxId, appId]) => (
                    <div key={boxId} className="flex justify-between">
                      <span className="text-blue-600">Box {boxId}:</span>
                      <span className="text-green-600">{appId}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Status */}
            <div>
              <h4 className="font-semibold text-gray-700 mb-1">Status</h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span>Multiple Apps:</span>
                  <span className={hasMultiApps ? 'text-green-600' : 'text-gray-500'}>
                    {hasMultiApps ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Total Apps:</span>
                  <span className="text-blue-600">{userApps.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Mapped Boxes:</span>
                  <span className="text-blue-600">{Object.keys(boxToAppMap).length}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div>
              <h4 className="font-semibold text-gray-700 mb-1">Actions</h4>
              <div className="space-y-1">
                <button
                  onClick={() => {
                    localStorage.removeItem('user_applications');
                    localStorage.removeItem('box_to_app_map');
                    window.location.reload();
                  }}
                  className="w-full bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
                >
                  Clear Multi-App Data
                </button>
                <button
                  onClick={() => {
                    const data = {
                      userApplications: userApps,
                      boxToAppMap: boxToAppMap,
                      hasMultipleApps: hasMultiApps
                    };
                    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
                    alert('Debug data copied to clipboard!');
                  }}
                  className="w-full bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                >
                  Copy Debug Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiAppDebug;
